import { EnvelopeIcon, PhoneIcon } from '@heroicons/react/24/outline';

import type { ContactHandleSchema } from '@/services/api/generated/subjectsService';

interface ContactHandleListProps {
  handles: ContactHandleSchema[];
  /** Show only primary phone/email (compact mode) */
  compact?: boolean;
}

/**
 * Renders contact handles (phone, email) with consistent styling
 */
export const ContactHandleList = ({
  handles,
  compact = false,
}: ContactHandleListProps) => {
  const phoneHandles = handles.filter((h) => h.handle_type === 'PHONE');
  const emailHandles = handles.filter((h) => h.handle_type === 'EMAIL');

  const primaryPhone = phoneHandles.find((h) => h.preferred) || phoneHandles[0];
  const primaryEmail = emailHandles.find((h) => h.preferred) || emailHandles[0];

  if (compact) {
    // Compact: show only primary phone and email
    if (!primaryPhone && !primaryEmail) return null;

    return (
      <div className="flex flex-wrap gap-x-3 gap-y-1 text-sm">
        {primaryPhone && (
          <div className="flex items-center gap-1.5">
            <PhoneIcon className="h-3.5 w-3.5 text-gray-400" />
            <span className="text-gray-900">{primaryPhone.value}</span>
          </div>
        )}
        {primaryEmail && (
          <div className="flex items-center gap-1.5">
            <EnvelopeIcon className="h-3.5 w-3.5 text-gray-400" />
            <span className="text-gray-900">{primaryEmail.value}</span>
          </div>
        )}
      </div>
    );
  }

  // Full: show all handles with details
  if (phoneHandles.length === 0 && emailHandles.length === 0) return null;

  return (
    <div className="space-y-2">
      <div className="text-xs font-medium text-gray-500 uppercase tracking-wide">
        Contact
      </div>
      <div className="space-y-2">
        {phoneHandles.map((h, i) => (
          <div key={h.id || i} className="flex items-start gap-2 text-sm">
            <PhoneIcon className="h-4 w-4 text-gray-400 flex-shrink-0 mt-0.5" />
            <div className="flex-1 min-w-0">
              {h.name && (
                <div className="text-xs text-gray-500">
                  {h.name}
                  {h.preferred && (
                    <span className="ml-1 text-blue-600">· preferred</span>
                  )}
                </div>
              )}
              <div className="text-gray-900 font-medium">{h.value}</div>
              {h.notes && (
                <div className="text-xs text-gray-600 mt-0.5">{h.notes}</div>
              )}
            </div>
          </div>
        ))}
        {emailHandles.map((h, i) => (
          <div key={h.id || i} className="flex items-start gap-2 text-sm">
            <EnvelopeIcon className="h-4 w-4 text-gray-400 flex-shrink-0 mt-0.5" />
            <div className="flex-1 min-w-0">
              {h.name && (
                <div className="text-xs text-gray-500">
                  {h.name}
                  {h.preferred && (
                    <span className="ml-1 text-blue-600">· preferred</span>
                  )}
                </div>
              )}
              <div className="text-gray-900 font-medium">{h.value}</div>
              {h.notes && (
                <div className="text-xs text-gray-600 mt-0.5">{h.notes}</div>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};
